/**
 * Per-page SEO meta-tag updater.
 *
 * Updates document.title, description, Open Graph and Twitter card tags so
 * that LinkedIn/WhatsApp/Twitter previews show the right title/excerpt/image
 * for whichever page the visitor lands on.
 *
 * The default tags live in /public/index.html. This helper just overrides
 * them per route via useEffect.
 *
 * Usage:
 *   useEffect(() => {
 *     setMetaTags({
 *       title: 'Pricing — InterviewWala',
 *       description: 'Free forever for solo recruiters...',
 *       url: '/pricing',                         // path or full URL
 *       image: '/demo/01-dashboard.jpg',         // path or full URL
 *       type: 'website',                         // or 'article' for blog
 *     });
 *   }, []);
 */

const setMeta = (selector, content) => {
  if (typeof document === 'undefined') return;
  const el = document.querySelector(selector);
  if (el) el.setAttribute('content', content);
};

const setHref = (selector, href) => {
  if (typeof document === 'undefined') return;
  const el = document.querySelector(selector);
  if (el) el.setAttribute('href', href);
};

const absUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  if (typeof window === 'undefined') return path;
  return window.location.origin + path;
};

export const setMetaTags = ({
  title,
  description,
  image,
  url,
  type = 'website',
} = {}) => {
  if (title) {
    document.title = title;
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[name="twitter:title"]', title);
  }

  if (description) {
    setMeta('meta[name="description"]', description);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[name="twitter:description"]', description);
  }

  if (image) {
    const abs = absUrl(image);
    setMeta('meta[property="og:image"]', abs);
    setMeta('meta[name="twitter:image"]', abs);
  }

  if (url) {
    const abs = absUrl(url);
    setMeta('meta[property="og:url"]', abs);
    setHref('link[rel="canonical"]', abs);
  }

  setMeta('meta[property="og:type"]', type);
};
